import { ChangeDetectorRef, Component, EventEmitter, Input, Output } from '@angular/core'
import { ChangeRequest } from '@app/core/model/change-request.model'
import { ChangeRequestStatus } from '@app/core/model/change-request-statuses'
import { User } from '@app/core/model/user.model'
import { ScopeVersion } from '@app/core/model/scope-version'
import { FormControl } from '@angular/forms'
import { MatDialog, MatDialogRef } from '@angular/material/dialog'
import { ModalConfig } from '@app/core/model/modal-config.model'
import { ScopeUiModalComponent } from '@app/shared/components/ui-components/scope-ui-modal/scope-ui-modal.component'
import { SNACKBAR_LENGTH_SHORT, SnackbarService } from '@app/shared/utils/snackbar.service'
import { Observable } from 'rxjs'
import { untilDestroyed } from '@app/shared/utils/utils'
import { ApprovalFlowService } from '@app/features/scope-overview/service/approval-flow.service'
import { ChangeRequestService } from '../../service'
import { ChangeRequestActions } from '@app/features/scope-overview/change-request/store/actions/change-request.actions'
import { ScopePaginatedOverviewActions } from '@app/features/scope-overview/store/actions/scope-paginated-overview.actions'
import { Store } from '@ngrx/store'
import { Router } from '@angular/router'

@Component({
  selector: 'change-request-approval-flow',
  templateUrl: './change-request-approval-flow.component.html',
  styleUrls: ['./change-request-approval-flow.component.scss'],
})
export class ChangeRequestApprovalFlowComponent {
  @Input() changeRequest!: ChangeRequest
  @Input() currentUser!: User
  @Input() collaborators: User[] = []
  @Input() parentScope!: ScopeVersion

  @Output() onTraffic!: EventEmitter<void>

  protected readonly ChangeRequestStatus = ChangeRequestStatus
  protected readonly MAX_VISIBLE_COLLABORATORS = 2

  constructor(
    private dialog: MatDialog,
    private snackbarService: SnackbarService,
    private cdr: ChangeDetectorRef,
    private scopeApprovalFlowService: ApprovalFlowService,
    private changeRequestService: ChangeRequestService,
    private store: Store,
    private router: Router
  ) {
    this.onTraffic = new EventEmitter<void>()
    this.destroy$ = untilDestroyed()
  }

  submitDraft() {
    this.openChangeRequestModal({
      title: 'Submit change request',
      body: 'Are you sure you want to submit this change request for client approval?',
      confirmText: 'Submit',
      successMessage: 'Change Request submitted',
      status: ChangeRequestStatus.CLIENT_APPROVED,
      apiCall: (scopeId, crId) => this.changeRequestService.submitChangeRequest(scopeId, crId),
      requireComment: false,
    })
  }

  dismiss() {
    this.openChangeRequestModal({
      title: 'Dismiss change request',
      body: 'Add a note here to help other collaborators understand why this change request is being dismissed',
      confirmText: 'Dismiss',
      successMessage: 'Change Request dismissed',
      status: ChangeRequestStatus.UNMERGED_CLOSED,
      apiCall: (scopeId, crId, comment) => this.changeRequestService.dismissChangeRequest(scopeId, crId, { comment }),
      requireComment: true,
    })
  }

  mergeToScope() {
    this.openChangeRequestModal({
      title: 'Merge change request',
      body: 'Add a note here to help other collaborators understand your work on this change request before merging it to scope',
      confirmText: 'Merge',
      successMessage: 'Change Request merged to scope',
      status: ChangeRequestStatus.MERGED_CLOSED,
      apiCall: (scopeId, crId, comment) => this.changeRequestService.mergeChangeRequest(scopeId, crId, { comment }),
      requireComment: false,
      onSuccess: () => {
        this.store.dispatch(ChangeRequestActions.resetCurrentChangeRequest())
        this.store.dispatch(ScopePaginatedOverviewActions.getCurrentScope({ scopeId: this.parentScope.identity.id }))
        this.router.navigateByUrl(`/scopes/scope-overview/${this.parentScope.identity.id}`)
      },
    })
  }

  backToDraft() {
    this.openChangeRequestModal({
      title: 'Move change request to draft',
      body: 'Add a note here to help other collaborators understand why this change request is being moved back to draft',
      confirmText: 'Move to draft',
      successMessage: 'Change Request moved back to draft',
      status: ChangeRequestStatus.DRAFT,
      apiCall: (scopeId, crId, comment) =>
        this.changeRequestService.revertChangeRequestToDraft(scopeId, crId, { comment }),
      requireComment: false,
    })
  }

  openOutput(): void {
    // TODO: Implement if needed
  }

  openTraffic(): void {
    this.onTraffic.emit()
  }

  getInitials(user: User): string {
    if (!user) {
      return ''
    }

    const first = user.firstName?.charAt(0) ?? ''
    const last = user.lastName?.charAt(0) ?? ''
    return `${first}${last}`.toUpperCase()
  }

  getRemainingCollaborators(): number {
    return Math.max(this.collaborators.length - this.MAX_VISIBLE_COLLABORATORS, 0)
  }

  isDraftStatusActive(): boolean {
    return this.changeRequest?.status === ChangeRequestStatus.DRAFT
  }

  isClientApprovalActive(): boolean {
    return this.changeRequest?.status === ChangeRequestStatus.CLIENT_APPROVED
  }

  isStatusHighlighted(status: ChangeRequestStatus): boolean {
    if (!this.changeRequest) {
      return false
    }

    return this.changeRequest.status === status
  }

  canSubmit(): boolean {
    return this.changeRequest?.status === ChangeRequestStatus.DRAFT
  }

  canEditChangeRequest(): boolean {
    if (
      !this.parentScope ||
      !this.currentUser ||
      this.parentScope.identity.currentVersionNumber != this.parentScope.version
    ) {
      return false
    }
    return this.isUserScopeCollaborator()
  }

  private openChangeRequestModal(config: {
    title: string
    body: string
    confirmText: string
    successMessage: string
    status: ChangeRequestStatus
    apiCall: (scopeId: number, crId: number, comment: string) => Observable<ChangeRequest>
    requireComment?: boolean
    onSuccess?: () => void
  }): void {
    if (!this.changeRequest) return

    const modalConfig: ModalConfig = {
      title: config.title,
      body: config.body,
      confirmText: config.confirmText,
      confirmCallback: (form: FormControl) => {
        this.setApprovalLoading(true)
        const comment: string = form.get('comment')?.value?.trim() ?? ''

        if (config.requireComment && !comment) {
          this.setApprovalLoading(false)
          return
        }

        this.executeChangeRequestApiCall(
          config.apiCall(this.parentScope.identity.id, this.changeRequest!.id, comment),
          config.successMessage,
          dialog,
          config.status,
          config.onSuccess
        )
      },
      inputs: [{ name: 'comment', control: new FormControl(''), type: 'textarea', label: 'Add text here' }],
      isFormValid: config.requireComment ? (form: FormControl) => !!form.get('comment')?.value?.trim() : undefined,
      isLoading$: this.scopeApprovalFlowService.isApprovalLoading$,
      limitBodyWidth: true,
    }

    const dialog = this.dialog.open(ScopeUiModalComponent, { data: modalConfig })
  }

  private executeChangeRequestApiCall(
    apiCall: Observable<ChangeRequest>,
    successMessage: string,
    dialog: MatDialogRef<ScopeUiModalComponent, any>,
    status: ChangeRequestStatus,
    onSuccess?: () => void
  ): void {
    apiCall.pipe(this.destroy$()).subscribe({
      next: () => {
        this.setApprovalLoading(false)
        this.snackbarService.showInfoSnackbar(successMessage, SNACKBAR_LENGTH_SHORT)
        this.store.dispatch(ChangeRequestActions.updateCurrentChangeRequestStatus({ status }))
        dialog.close()
        onSuccess?.()
      },
      error: (error: unknown) => {
        this.setApprovalLoading(false)
        this.snackbarService.showDefaultErrorSnackbar()
        console.error(JSON.stringify(error))
      },
    })
  }

  private setApprovalLoading = (value: boolean) => {
    this.scopeApprovalFlowService.isApprovalLoadingSource$.next(value)
    this.cdr.detectChanges()
  }

  private isUserScopeCollaborator(): boolean {
    if (!this.parentScope?.identity?.team?.collaborators) return false

    return this.parentScope.identity.team.collaborators.some(
      (collaborator) => collaborator.user.id === this.currentUser.id
    )
  }

  private readonly destroy$
}
